import React, { useEffect, useRef, useState } from 'react';
import { ApiClientWrapper } from '@spike/api-client';
import Client from '@spike/client-model';
import { EnrollmentHistory } from '@spike/membership-model';
import { fetchMembershipClientPurchaseHistoryThunk, MembershipsStatus } from '@spike/memberships-action';
import { AxiosInstance } from 'axios';
import useApiClientWrapper from 'hooks/useApiClientWrapper';
import { useDebounce } from 'hooks/useDebounce';
import useIsMobile from 'hooks/useIsMobile';
import { useDispatch, useSelector } from 'react-redux';
import { ClientEditHeader } from 'components/Client/ClientEditHeader';
import SellMembership from 'components/Memberships/Sell/SellMembership';
import { Pagination } from 'components/UI';
import { RootState } from 'store';
import ClientMembershipsHeader from './ClientMembershipsHeader';
import ClientMembershipsTable from './ClientMembershipsTable';
import EnrollmentDetails from 'components/Memberships/Enrollment/EnrollmentDetails';
import {
    ClientMembershipsPagination,
    parsePurchaseHistoryPagination,
    unwrapPurchaseHistoryPayload
} from './parsePurchaseHistoryPagination';
import styles from './ClientMemberships.module.scss';

interface ClientMembershipsProps {
    client: Client;
    onBook: () => void;
    onClose: () => void;
    onSelect: (key: string) => void;
}

const wrapPurchaseHistoryClient = (
    apiClientWrapper: ApiClientWrapper,
    onPurchaseHistory: (data: unknown) => void
): ApiClientWrapper => ({
    ...apiClientWrapper,
    apiClient: {
        ...apiClientWrapper.apiClient,
        get: ((url, config) =>
            apiClientWrapper.apiClient.get(url, config).then(response => {
                if (String(url).includes('memberships_client_purchase_history')) {
                    onPurchaseHistory(response.data);
                }
                return response;
            })) as AxiosInstance['get']
    } as AxiosInstance
});

export const ClientMemberships: React.FC<ClientMembershipsProps> = ({ client, onBook, onClose, onSelect }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const isMobile = useIsMobile();
    const [selectedFilter, setSelectedFilter] = useState('active');
    const [searchText, setSearchText] = useState('');
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(10);
    const [selectedEnrollmentId, setSelectedEnrollmentId] = useState<number | null>(null);
    const [showSellMembership, setShowSellMembership] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [pagination, setPagination] = useState<ClientMembershipsPagination | null>(null);
    const fetchIdRef = useRef(0);
    const debouncedSearchText = useDebounce(searchText.trim(), 300);
    const status = selectedFilter === 'all' ? undefined : selectedFilter;
    const query = debouncedSearchText.length > 0 ? debouncedSearchText : undefined;
    const membershipsStatus = useSelector<RootState, MembershipsStatus>(state => state.memberships.status);
    const enrollments = useSelector<RootState, EnrollmentHistory[]>(state => state.memberships.enrollmentsHistory);
    const selectedFilterCount = isLoading ? null : pagination?.totalCount ?? 0;

    const refreshPurchaseHistory = () => {
        if (!client.id) return;

        const fetchId = ++fetchIdRef.current;

        dispatch(
            fetchMembershipClientPurchaseHistoryThunk(
                wrapPurchaseHistoryClient(apiClientWrapper, data => {
                    if (fetchId !== fetchIdRef.current) return;

                    const payload = unwrapPurchaseHistoryPayload(data);
                    const pageLength = Array.isArray(payload.enrollments) ? payload.enrollments.length : 0;
                    setPagination(parsePurchaseHistoryPagination(data, page, size, pageLength));
                }),
                client.id,
                page,
                size,
                status,
                undefined,
                query
            )
        );
    };
    const handleSellMembership = () => setShowSellMembership(true);
    const handleCloseSellMembership = () => setShowSellMembership(false);
    const handleMembershipPaid = () => {
        setTimeout(() => {
            refreshPurchaseHistory();
        }, 3000);
    };

    useEffect(() => {
        refreshPurchaseHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client.id, page, query, size, status]);

    useEffect(() => {
        switch (membershipsStatus) {
            case MembershipsStatus.GettingClientPurchaseHistoryStartAction:
                setIsLoading(true);
                break;
            case MembershipsStatus.GetClientPurchaseHistorySuccessAction:
            case MembershipsStatus.GetClientPurchaseHistoryErrorAction:
                setIsLoading(false);
                break;
            default:
                break;
        }
    }, [membershipsStatus]);

    return (
        <div className={styles.root}>
            {isMobile && (
                <div className={styles.editWrapper}>
                    <ClientEditHeader
                        client={client}
                        onSelect={onSelect}
                        onBook={onBook}
                        onClose={onClose}
                    />
                </div>
            )}

            <div className={styles.pageScroll}>
                <div className={styles.pageInner}>
                    <ClientMembershipsHeader
                        selectedFilter={selectedFilter}
                        selectedFilterCount={selectedFilterCount}
                        searchText={searchText}
                        onSellMembership={handleSellMembership}
                        onSelectFilter={value => {
                            setSelectedFilter(value);
                            setPage(1);
                        }}
                        onChangeSearchText={value => {
                            setSearchText(value);
                            setPage(1);
                        }}
                    />

                    <div className={styles.content}>
                        <ClientMembershipsTable
                            enrollments={enrollments}
                            loading={isLoading}
                            onRefresh={refreshPurchaseHistory}
                            onEnrollmentClick={enrollment => setSelectedEnrollmentId(enrollment.enrollmentId)}
                        />
                    </div>
                </div>
            </div>

            {pagination && !isLoading && (
                <div className={styles.floatingPagination}>
                    <Pagination
                        count={Math.max(pagination.totalCount, 1)}
                        page={pagination.currentPage || page}
                        pageSize={size}
                        totalPages={Math.max(pagination.totalPages, 1)}
                        hasNextPage={pagination.hasNextPage}
                        onPageChange={setPage}
                        onPageSizeChange={value => {
                            setSize(value);
                            setPage(1);
                        }}
                    />
                </div>
            )}

            {selectedEnrollmentId && (
                <EnrollmentDetails
                    enrollmentId={selectedEnrollmentId}
                    onClose={() => setSelectedEnrollmentId(null)}
                />
            )}

            {showSellMembership && (
                <SellMembership
                    clientId={client.id}
                    fromClientMemberships={true}
                    onClose={handleCloseSellMembership}
                    onPaid={handleMembershipPaid}
                />
            )}
        </div>
    );
};

export default ClientMemberships;
